/**
 * The Deal Room home's deal list: one row per deal, newest change first, each
 * linking to that deal's files. A row also does what the sidebar's deal row
 * does — add files (button or drop), delete the deal, and spin while files
 * are uploading or being read.
 */
import { Folder, Loader2, Plus, Trash2, Upload } from "lucide-react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import type { DealRow } from "../../hooks/useFileLibrary";
import {
	type DealActivity,
	type DealRoomActions,
	dragHasFiles,
} from "../../hooks/useDealRoomActions";
import { formatDate, formatDateTime } from "../../utils/formatUtils";
import { isOwnFolder, isSharedFolder } from "../../utils/folderSharing";
import OwnerAvatar from "./OwnerAvatar";

interface DealsTableProps {
	rows: DealRow[];
	actions: DealRoomActions;
	activityFor: (dealId: number, projectId: string | null | undefined) => DealActivity;
}

const iconButton =
	"relative z-10 flex h-[28px] w-[28px] flex-shrink-0 items-center justify-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004C4B]";

/**
 * One column template for the header and every row, so each heading sits
 * exactly over its column. The deal name is left-aligned; the other columns,
 * headings and values alike, are centred. Columns drop out at
 * the same breakpoints the cells hide at.
 */
const COLUMNS =
	"grid items-center gap-x-3.5 px-[18px] grid-cols-[34px_minmax(0,1fr)_50px_64px] md:grid-cols-[34px_minmax(0,1fr)_150px_50px_64px] lg:grid-cols-[34px_minmax(0,1fr)_150px_190px_50px_64px]";

const DealsTableRow: React.FC<
	DealRow & { actions: DealRoomActions; activity: DealActivity }
> = ({ deal, matchCount, actions, activity }) => {
	const [isDropTarget, setIsDropTarget] = useState(false);
	const addFiles = actions.addFilesTo(deal.node);
	// Deletes wait for an upload into the deal to finish.
	const uploading = actions.isUploadingInto(deal.id);
	// Only the owner deletes a deal, shared or not.
	const canDelete = isOwnFolder(deal.node.folder);
	const shared = isSharedFolder(deal.node.folder);
	const onDragOver = (e: React.DragEvent) => {
		if (!addFiles || !dragHasFiles(e)) return;
		e.preventDefault();
		e.dataTransfer.dropEffect = "copy";
		setIsDropTarget(true);
	};
	return (
		<div
			className={`group relative ${COLUMNS} border-b border-[#f1f2f4] py-3 last:border-b-0 ${
				isDropTarget
					? "bg-primary-50 outline-dashed outline-2 -outline-offset-2 outline-primary-500"
					: "hover:bg-[#f6fbfb]"
			}`}
			onDragEnter={onDragOver}
			onDragOver={onDragOver}
			onDragLeave={(e) => {
				if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
				setIsDropTarget(false);
			}}
			onDrop={(e) => {
				setIsDropTarget(false);
				actions.dropFilesOn(deal.node, e);
			}}
		>
			<span
				className={`flex h-[34px] w-[34px] items-center justify-center rounded-lg ${
					deal.files.length ? "bg-[#E6F3F3] text-[#004C4B]" : "bg-[#f2f2f2] text-gray-500"
				}`}
			>
				<Folder className="h-4 w-4" aria-hidden />
			</span>
			<div className="min-w-0">
				<div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
					{/* The whole row is the link: its ::after covers the row, and
					    the action buttons sit above it. */}
					<Link
						to={`/files/deals/${deal.id}`}
						className="truncate after:absolute after:inset-0 after:content-[''] focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-[#004C4B]"
					>
						{deal.name}
					</Link>
					{shared && (
						<OwnerAvatar name={deal.node.folder.owner?.name} label="Owned by" />
					)}
					{activity.busy && (
						<Loader2
							className="h-3 w-3 flex-shrink-0 animate-spin text-[#2A9D8F]"
							aria-label={
								activity.readingCount > 0
									? `${activity.readingCount} file${activity.readingCount === 1 ? "" : "s"} still being processed`
									: "Processing files"
							}
						/>
					)}
				</div>
				<div className="mt-0.5 truncate text-xs text-text-muted">
					{isDropTarget
						? "Let go to add these files to this deal"
						: matchCount !== undefined
							? `${matchCount} matching file${matchCount === 1 ? "" : "s"}`
							: `Created ${formatDate(deal.node.folder.created_at)}`}
				</div>
			</div>
			<span className="hidden items-center justify-center gap-1.5 text-xs text-gray-600 md:flex">
				<Upload className="h-3.5 w-3.5" aria-hidden />
				Uploaded
			</span>
			<span className="hidden truncate text-center text-[13px] text-gray-700 lg:block">
				{formatDateTime(deal.lastChangeAt)}
			</span>
			<span className="text-center text-[13px] font-semibold">{deal.files.length}</span>
			<div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100">
				{addFiles && (
					<button
						type="button"
						aria-label={`Add files to ${deal.name}`}
						title="Add files"
						onClick={() => addFiles()}
						className={`${iconButton} border border-[#99CFCD] bg-white text-[#004C4B] hover:bg-[#f6fbfb]`}
					>
						<Plus className="h-3.5 w-3.5" aria-hidden />
					</button>
				)}
				{canDelete && (
					<button
						type="button"
						aria-label={`Delete ${deal.name}`}
						title={uploading ? "Files are still being added to this deal. You can delete once that finishes." : "Delete deal"}
						disabled={uploading}
						onClick={() =>
							actions.requestDeleteFolder({ node: deal.node, isDeal: true, dealId: deal.id })
						}
						className={`${iconButton} text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent`}
					>
						<Trash2 className="h-3.5 w-3.5" aria-hidden />
					</button>
				)}
			</div>
		</div>
	);
};

const DealsTable: React.FC<DealsTableProps> = ({ rows, actions, activityFor }) => (
	<div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
		<div
			className={`${COLUMNS} border-b border-gray-200 bg-[#fafafa] py-2.5 text-xs font-semibold text-[#5B6472]`}
		>
			<span />
			<span>Deal</span>
			<span className="hidden text-center md:block">Files from</span>
			<span className="hidden text-center lg:block">Last change</span>
			<span className="text-center">Files</span>
			<span />
		</div>
		{rows.map((row) => (
			<DealsTableRow
				key={row.deal.id}
				{...row}
				actions={actions}
				activity={activityFor(row.deal.id, row.deal.node.folder.project_id)}
			/>
		))}
	</div>
);

export default DealsTable;
